import { useState } from 'react';

import { ApiError } from '../../api/client';
import { useCreateUser } from '../../api/admin';
import type { Role } from '../../auth/roles';
import { roleLabel } from '../../auth/roles';
import { Combobox } from '../../components/Combobox';
import type { ComboboxOption } from '../../components/combobox-model';
import { Button, Drawer, Field, Input, Select, Spinner } from '../../components/ui';
import { departmentSelectOptions } from '../../departments';
import type { ManagerDepartment } from '../../departments';
import { useVenueDirectory } from '../quotas/dictionary';
import { venueComboboxOptions } from '../quotas/quotas-model';
import {
  ROLE_OPTIONS,
  USER_CURATOR_VENUES_HINT,
  USER_CURATOR_VENUES_LABEL,
  USER_MANAGER_VENUES_HINT,
  USER_MANAGER_VENUES_LABEL,
  emptyUserCreateDraft,
  userCreateFrom,
  withVenue,
  withoutVenue,
  type UserCreateDraft,
} from './settings-model';

/**
 * «Добавить пользователя» — the create half of contract #25.1 (ADDITIVE v1.8.0,
 * wave 9, T.Y9; A14 reworked in wave 10, T.Z13). There is NO self-registration
 * (D20/Р21): every account exists because an ADMIN made it.
 *
 * The fields, in the order the form asks them (A14):
 *   * E-mail + отображаемое имя — the required pair (a missing one is a 400).
 *   * Роль — a RADIO, one of the three (D21: exactly one role per user), МЕНЕДЖЕР
 *     by default. An АДМИН grant is a rare, deliberate act, so it is one click
 *     away, not the first option.
 *   * Привязки площадок — the set the chosen role allows: «Площадки» for a
 *     МЕНЕДЖЕР (`venueIds`), «Куратор площадок» for a КУРАТОР (`curatorVenueIds`).
 *     One role, one matching link set: the crossed set is never sent (the api
 *     refuses it), and a role with NO venue is refused twice — here, as a client
 *     hint, and on the api as a 400 that this form surfaces with the same hint.
 *     An АДМИН gets no picker on purpose: either link kind is optional for an
 *     ADMIN, and the set is edited after the fact from the venue card / drawer.
 *
 * The note the form carries is a promise about what does NOT happen (and, since
 * D21, about what DOES): no invitation letter is sent, and the person may sign in
 * BOTH ways — the Yandex account their e-mail matches, or the code request sent
 * to the address just linked.
 *
 * After a 201 the roster re-reads itself (`useCreateUser` invalidates it) and the
 * drawer closes — the new row is the roster's to show, not this form's.
 */
export function UserCreateDrawer({ onClose }: { onClose: () => void }) {
  const create = useCreateUser();
  const [draft, setDraft] = useState<UserCreateDraft>(emptyUserCreateDraft);
  const [errors, setErrors] = useState<{
    email?: string;
    displayName?: string;
    role?: string;
    venues?: string;
  }>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const directory = useVenueDirectory();

  // The one picker the role allows: a МЕНЕДЖЕР stages manager links, a КУРАТОР
  // curator links, an АДМИН neither (see the header note).
  const staged = draft.role === 'MANAGER' ? draft.managerVenues : draft.curatorVenues;
  const stagedIds = new Set(staged.map((venue) => venue.venueId));
  const options = venueComboboxOptions((directory.data ?? []).filter((venue) => !stagedIds.has(venue.id)));

  const set = (patch: Partial<UserCreateDraft>) => {
    setServerError(null);
    setDraft((current) => ({ ...current, ...patch }));
  };

  const submit = () => {
    const built = userCreateFrom(draft);
    setErrors(built.errors);
    if (built.input === null) return;
    setServerError(null);
    create.mutate(built.input, {
      onSuccess: onClose,
      onError: (cause) => setServerError(describeCreateError(cause)),
    });
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Новый пользователь"
      description="Аккаунт создаётся до первого входа — человек получит доступ, когда войдёт."
    >
      <div className="user-drawer" data-testid="user-create-drawer">
        <section className="user-drawer__section" aria-label="Новый пользователь">
          <form
            data-testid="user-create-form"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <Field
              label="E-mail"
              htmlFor="user-create-email"
              required
              error={errors.email}
              hint="Хранится строчными буквами; по этому адресу приходит код входа."
            >
              <Input
                id="user-create-email"
                data-testid="user-create-email"
                type="email"
                value={draft.email}
                aria-invalid={errors.email !== undefined}
                placeholder="curator@example.ru"
                onChange={(event) => set({ email: event.currentTarget.value })}
              />
            </Field>

            <Field
              label="Отображаемое имя"
              htmlFor="user-create-name"
              required
              error={errors.displayName}
              hint="До первого входа заменяет подпись в списках."
            >
              <Input
                id="user-create-name"
                data-testid="user-create-name"
                value={draft.displayName}
                aria-invalid={errors.displayName !== undefined}
                placeholder="Иванова Анна"
                onChange={(event) => set({ displayName: event.currentTarget.value })}
              />
            </Field>

            {/* A14 — the role is a RADIO, not a select: one role per user (D21) is a
                choice the operator makes once and reads back at a glance, and the
                picker below changes with it. */}
            <fieldset className="user-role-picker" data-testid="user-create-role-picker">
              <legend className="user-drawer__label">
                Роль
                <span className="field__required" aria-hidden="true">
                  {' '}
                  *
                </span>
              </legend>
              {errors.role ? (
                <p className="field__error" role="alert" data-testid="user-create-role-error">
                  {errors.role}
                </p>
              ) : null}
              {ROLE_OPTIONS.map((role) => (
                <label className="user-role-picker__option" key={role}>
                  <input
                    type="radio"
                    name="user-create-role"
                    data-testid={`user-create-role-${role}`}
                    value={role}
                    checked={draft.role === role}
                    onChange={() => set({ role: role as Role })}
                  />
                  <span>{roleLabel(role)}</span>
                </label>
              ))}
              <p className="user-drawer__hint">Роль одна — привязки площадок ниже должны ей соответствовать.</p>
            </fieldset>

            {/* ADDITIVE (T.Z42) — the МЕНЕДЖЕР's department, optional. The option texts
                are the FULL department names (the owner's wording); «— без отдела»
                leaves the column NULL, which is the default today. */}
            {draft.role === 'MANAGER' ? (
              <Field
                label="Отдел"
                htmlFor="user-create-department"
                hint="Отдел привлечения или Отдел сопровождения — по нему собираются фильтры площадок и дашборда."
              >
                <Select
                  id="user-create-department"
                  data-testid="user-create-department"
                  value={draft.department ?? ''}
                  onChange={(event) =>
                    set({ department: event.target.value === '' ? null : (event.target.value as ManagerDepartment) })
                  }
                >
                  {departmentSelectOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}

            {draft.role === 'MANAGER' || draft.role === 'VIEWER' ? (
              <VenuePicker
                role={draft.role}
                staged={staged}
                errors={errors}
                options={options}
                directory={directory.data ?? []}
                onAdd={(venueId) => {
                  const entry = (directory.data ?? []).find((venue) => venue.id === venueId);
                  if (entry === undefined) return;
                  set(
                    draft.role === 'MANAGER'
                      ? { managerVenues: withVenue(draft.managerVenues, { venueId: entry.id, venueName: entry.name }) }
                      : { curatorVenues: withVenue(draft.curatorVenues, { venueId: entry.id, venueName: entry.name }) },
                  );
                }}
                onRemove={(venueId) => {
                  set(
                    draft.role === 'MANAGER'
                      ? { managerVenues: withoutVenue(draft.managerVenues, venueId) }
                      : { curatorVenues: withoutVenue(draft.curatorVenues, venueId) },
                  );
                }}
              />
            ) : null}

            {serverError ? (
              <p className="user-role-error" role="alert" data-testid="user-create-error">
                {serverError}
              </p>
            ) : null}

            <div className="user-drawer__footer">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={create.isPending}
                data-testid="user-create-submit"
              >
                Создать
              </Button>
              <Button type="button" size="sm" data-testid="user-create-cancel" onClick={onClose}>
                Отмена
              </Button>
            </div>
          </form>

          <p className="settings-note" data-testid="user-create-note">
            Письмо-приглашение не отправляется: пользователь может войти и через Яндекс, и по коду с этого адреса.
            Создание записывается в журнал аудита (USER_CREATED).
            {create.isPending ? <Spinner label="Создаём" /> : null}
          </p>
        </section>
      </div>
    </Drawer>
  );
}

/**
 * The link set of the chosen role — «Площадки» for a МЕНЕДЖЕР, «Куратор площадок»
 * for a КУРАТОР (the same words the user drawer's sections use, so one term is
 * one set everywhere). A14: the empty list is a HINT, not an error, until the
 * submit runs — then the missing set stops the submit before a round trip.
 */
function VenuePicker({
  role,
  staged,
  errors,
  options,
  directory,
  onAdd,
  onRemove,
}: {
  role: Role;
  staged: ReadonlyArray<{ venueId: number; venueName: string }>;
  errors: { venues?: string };
  options: ComboboxOption[];
  directory: ReadonlyArray<{ id: number; name: string }>;
  onAdd: (venueId: number) => void;
  onRemove: (venueId: number) => void;
}) {
  const isManager = role === 'MANAGER';
  const label = isManager ? USER_MANAGER_VENUES_LABEL : USER_CURATOR_VENUES_LABEL;
  const emptyHint = isManager
    ? 'Площадок не выбрано — выберите хотя бы одну, иначе аккаунт не примется.'
    : 'Площадок не выбрано — без ссылки вход по коду не сработает.';
  const hint = isManager ? USER_MANAGER_VENUES_HINT : USER_CURATOR_VENUES_HINT;

  return (
    <div className="user-drawer__field">
      <span className="user-drawer__label" data-testid="user-create-venues-label">
        {label} ({staged.length})
      </span>
      {staged.length > 0 ? (
        <ul className="user-drawer__venues" data-testid="user-create-venues">
          {staged.map((venue) => (
            <li
              className="user-drawer__venue"
              key={venue.venueId}
              data-testid="user-create-venue"
              data-venue-id={venue.venueId}
            >
              <span className="user-drawer__venue-name" title={venue.venueName}>
                {venue.venueName}
              </span>
              <button
                type="button"
                className="icon-btn"
                aria-label={`Убрать ${venue.venueName}`}
                data-testid={`user-create-venue-remove-${venue.venueId}`}
                onClick={() => onRemove(venue.venueId)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="user-drawer__hint" data-testid="user-create-venues-empty">
          {emptyHint}
        </p>
      )}
      <div className="user-drawer__add">
        <Combobox
          id="user-create-venue-add"
          testId="user-create-venue-add"
          value=""
          options={options}
          placeholder={`Добавить в «${label}» — найдите по названию или адресу`}
          emptyText="Площадка не найдена"
          clearLabel="Очистить выбор площадки"
          ariaLabel={`Добавить площадку — ${label}`}
          resultsLabel={(count) => `Найдено площадок: ${count}`}
          onChange={(next) => {
            const id = Number.parseInt(next, 10);
            if (!Number.isFinite(id) || !directory.some((venue) => venue.id === id)) return;
            onAdd(id);
          }}
        />
      </div>
      {errors.venues ? (
        <p className="user-role-error" role="alert" data-testid="user-create-venues-error">
          {errors.venues}
        </p>
      ) : (
        <p className="user-drawer__hint">{hint}</p>
      )}
    </div>
  );
}

/** The api's refusals, in the words the operator acts on. */
function describeCreateError(error: unknown): string {
  if (error instanceof ApiError && error.is('CONFLICT', 409)) {
    return 'Пользователь с таким адресом уже есть.';
  }
  if (error instanceof ApiError && error.is('VALIDATION', 400)) {
    // The api's own D21 rule, restated next to the field it is about: a role with
    // no venue is the 400 an operator actually meets here.
    return `Проверьте поля: адрес, имя и привязки площадок. ${USER_CURATOR_VENUES_HINT}`;
  }
  return error instanceof Error ? error.message : 'Не удалось создать пользователя.';
}
